import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { AlertOctagon, CheckCircle2, OctagonX, PlayCircle, AlertTriangle } from 'lucide-react';
import { api } from '../../api/client';
import { qk } from '../../api/hooks';
import { isConflict } from '../../api/errors';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorAlert, QueryBoundary } from '../../components/States';
import { ToneBadge } from '../../components/StatusBadge';
import { formatDateTime } from '../../lib/format';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' }, gap: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ minWidth: 0 }}>
        {children}
      </Typography>
    </Box>
  );
}

export function OperationsTab() {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<'stop' | 'resume' | null>(null);
  const q = useQuery({
    queryKey: qk.operations,
    queryFn: api.operations,
    refetchInterval: (query) => (query.state.data && !query.state.data.executionGate.confirmed ? 2000 : 30000),
  });
  const gate = useMutation({
    mutationFn: (enabled: boolean) => api.setExecutionGate(enabled, q.data!.executionGate.version),
    onSuccess: (d) => {
      qc.setQueryData(qk.operations, d);
      void qc.invalidateQueries({ queryKey: qk.me });
      setConfirm(null);
    },
    onError: (err) => {
      if (isConflict(err)) void qc.invalidateQueries({ queryKey: qk.operations });
    },
  });
  const o = q.data;
  const executionOn = !!o && o.executionGate.desiredEnabled && o.executionGate.envEnabled;
  return (
    <QueryBoundary query={q} action="Betriebsdaten laden" loadingRows={6}>
      {o && (
        <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, alignItems: 'start' }}>
          <Card sx={{ gridColumn: { lg: '1 / -1' }, borderColor: executionOn ? undefined : 'var(--color-danger)' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" component="h3" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <AlertOctagon size={20} aria-hidden /> Globaler Not-Stopp (Ausführung für alle Gesellschaften)
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, mb: 2, maxWidth: 900 }}>
                Sperrt neue Schreiboperationen (Versand und Lösen) für manuelle und automatische Läufe aller Gesellschaften. Auswahl, Pre-Heating und Prüfung bleiben möglich. Bereits von Nidus angenommene Operationen werden weiter abgeglichen. Das ist unabhängig vom Gesellschaftsschalter „Automatische Läufe“.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {executionOn ? (
                  <ToneBadge tone="success" label="Ausführung freigegeben" />
                ) : (
                  <ToneBadge tone="error" label="Not-Stopp aktiv – keine Ausführung" data-testid="gate-stopped" />
                )}
                {!o.executionGate.confirmed && <ToneBadge tone="info" label="Bestätigung durch Nidus ausstehend" spinning />}
                {!o.executionGate.envEnabled && <ToneBadge tone="error" label="Per Betriebskonfiguration (EXECUTION_ENABLED=false) gesperrt" />}
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Version {o.executionGate.version}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {o.executionGate.desiredEnabled ? (
                  <Button variant="contained" color="error" startIcon={<OctagonX size={18} />} onClick={() => setConfirm('stop')} data-testid="emergency-stop">
                    Globalen Not-Stopp auslösen
                  </Button>
                ) : (
                  <Button variant="outlined" startIcon={<PlayCircle size={18} />} onClick={() => setConfirm('resume')} disabled={!o.executionGate.envEnabled}>
                    Ausführung wieder freigeben
                  </Button>
                )}
              </Box>
              {!o.executionGate.confirmed && !o.executionGate.desiredEnabled && (
                <Alert severity="warning" icon={<AlertTriangle size={18} />} sx={{ mt: 2 }}>
                  Lokal gesperrt. Bis Nidus die Sperre bestätigt, kann eine bereits unterwegs befindliche Anfrage noch angenommen werden.
                </Alert>
              )}
              <Box sx={{ mt: 2 }}>
                <ErrorAlert error={gate.error} action="Not-Stopp ändern" onClose={() => gate.reset()} />
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                Instanzbindung und Nidus
              </Typography>
              <Row label="Installation">{o.installation.name}</Row>
              <Row label="Nidus-Instanz-ID">
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {o.installation.nidusInstanceId}
                </Typography>
              </Row>
              <Row label="Nidus-URL">
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {o.installation.nidusBaseUrl}
                </Typography>
              </Row>
              <Row label="Handshake">
                {o.handshake.ok ? (
                  <Typography variant="body2" sx={{ color: 'success.dark', display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <CheckCircle2 size={16} /> Kompatibel
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: 'error.dark', display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <AlertOctagon size={16} /> Nicht kompatibel – Ausführung blockiert
                  </Typography>
                )}
                {o.handshake.message && (
                  <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                    {o.handshake.message}
                  </Typography>
                )}
              </Row>
              <Row label="API-Version">{o.handshake.apiVersion ?? '–'}</Row>
              <Row label="Zuletzt geprüft">{formatDateTime(o.handshake.checkedAt)}</Row>
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                Warteschlange und Versand
              </Typography>
              <Row label="Wartende Jobs">{o.queue.queued}</Row>
              <Row label="Laufende Jobs">{o.queue.running}</Row>
              <Row label="Endgültig fehlgeschlagen">
                {o.queue.dead > 0 ? <ToneBadge tone="error" label={`${o.queue.dead} Jobs`} /> : '0'}
              </Row>
              <Row label="Ältester wartender Job">{o.queue.oldestQueuedAgeSeconds === null ? '–' : `${Math.round(o.queue.oldestQueuedAgeSeconds)} s`}</Row>
              <Row label="Ungeklärte Zustellungen">
                {o.unknownDeliveries > 0 ? (
                  <ToneBadge tone="error" label={`${o.unknownDeliveries} klärungsbedürftig`} data-testid="unknown-deliveries" />
                ) : (
                  '0'
                )}
              </Row>
              {o.unknownDeliveries > 0 && (
                <Alert severity="error" icon={<AlertOctagon size={18} />} sx={{ mt: 2 }}>
                  <AlertTitle sx={{ fontWeight: 700 }}>Versand ungeklärt</AlertTitle>
                  Diese Tickets bleiben gesperrt, bis der Versandstatus in Nidus geklärt ist. Es wird nichts automatisch wiederholt.
                </Alert>
              )}
            </CardContent>
          </Card>
          <ConfirmDialog
            open={confirm === 'stop'}
            title="Globalen Not-Stopp auslösen?"
            confirmLabel="Not-Stopp auslösen"
            destructive
            busy={gate.isPending}
            onCancel={() => setConfirm(null)}
            onConfirm={() => gate.mutate(false)}
          >
            Ab sofort werden keine neuen Antworten versendet und keine Tickets gelöst – weder manuell noch automatisch. Bereits von Nidus angenommene Operationen werden weiter abgeglichen.
          </ConfirmDialog>
          <ConfirmDialog
            open={confirm === 'resume'}
            title="Ausführung wieder freigeben?"
            confirmLabel="Freigeben"
            busy={gate.isPending}
            onCancel={() => setConfirm(null)}
            onConfirm={() => gate.mutate(true)}
          >
            Manuell gestartete und aktivierte automatische Läufe dürfen danach wieder versenden und lösen.
          </ConfirmDialog>
        </Box>
      )}
    </QueryBoundary>
  );
}
